import OneSignal from "react-onesignal";
import { Capacitor } from "@capacitor/core";
import type { UserChangeEvent } from "react-onesignal";
import { supabase } from "@/integrations/supabase/client";

let hasInitialized = false;
let hasAttachedNativeDebugListeners = false;
let hasAttachedWebDebugListeners = false;
let nativePermissionRequestInFlight: Promise<boolean | null> | null = null;
const FLOW_LOG_PREFIX = "[OneSignalFlow]";

// NativeOneSignal is only used on iOS/Android — defined inline to avoid
// Vite trying to resolve onesignal-cordova-plugin on web builds.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NativeOneSignal = any;

const logFlow = (message: string, context?: Record<string, unknown>) => {
  if (context) {
    console.log(`${FLOW_LOG_PREFIX} ${message}`, context);
    return;
  }
  console.log(`${FLOW_LOG_PREFIX} ${message}`);
};

const getNativeOneSignal = async (): Promise<NativeOneSignal | null> => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    // Use indirect import string so Vite's static analysis skips this on web.
    const pluginId = "onesignal-cordova-plugin";
    const module = await import(/* @vite-ignore */ pluginId);
    return module.default;
  } catch (error) {
    console.error(`${FLOW_LOG_PREFIX} Failed to load onesignal-cordova-plugin`, error);
    return null;
  }
};

const attachNativeDebugListeners = (nativeOneSignal: NativeOneSignal) => {
  if (hasAttachedNativeDebugListeners) return;

  nativeOneSignal.User.addEventListener("change", (event) => {
    logFlow("Native User change event", {
      onesignalId: event.current?.onesignalId ?? null,
      externalId: event.current?.externalId ?? null,
    });
  });

  nativeOneSignal.User.pushSubscription.addEventListener("change", (event) => {
    logFlow("Native PushSubscription change event", {
      id: event.current?.id ?? null,
      tokenPresent: Boolean(event.current?.token),
      optedIn: event.current?.optedIn ?? false,
    });
  });

  nativeOneSignal.Notifications.addEventListener("permissionChange", (granted) => {
    logFlow("Native notification permission changed", { granted });
  });

  hasAttachedNativeDebugListeners = true;
};

const attachWebDebugListeners = () => {
  if (hasAttachedWebDebugListeners) return;

  const onWebUserChange = (event: UserChangeEvent) => {
    logFlow("Web User change event", {
      onesignalId: event.current?.onesignalId ?? null,
      pushSubscriptionId: OneSignal.User.PushSubscription.id ?? null,
    });
  };

  OneSignal.User.addEventListener("change", onWebUserChange);
  hasAttachedWebDebugListeners = true;
};

export const initializeOneSignal = async (): Promise<void> => {
  if (hasInitialized) {
    logFlow("initializeOneSignal skipped: already initialized");
    return;
  }

  const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
  const isNative = Capacitor.isNativePlatform();
  logFlow("initializeOneSignal start", {
    isNative,
    hasWindow: typeof window !== "undefined",
    hasAppId: Boolean(appId),
  });
  if (!appId || typeof window === "undefined") {
    logFlow("initializeOneSignal aborted: missing app id or window");
    return;
  }

  try {
    if (isNative) {
      const nativeOneSignal = await getNativeOneSignal();
      if (!nativeOneSignal) return;

      nativeOneSignal.Debug.setLogLevel(import.meta.env.DEV ? 6 : 4);
      nativeOneSignal.initialize(appId);
      attachNativeDebugListeners(nativeOneSignal);
      hasInitialized = true;
      logFlow("Native OneSignal initialized", { appId });

      if (import.meta.env.VITE_ONESIGNAL_AUTO_PROMPT === "true") {
        const canRequest = await nativeOneSignal.Notifications.canRequestPermission();
        logFlow("Native permission prompt check", { canRequest });
        if (canRequest) {
          const granted = await nativeOneSignal.Notifications.requestPermission(false);
          logFlow("Native permission prompt result", { granted });
        }
      }
      return;
    }

    await OneSignal.init({
      appId,
      allowLocalhostAsSecureOrigin: import.meta.env.DEV,
      serviceWorkerPath: "/OneSignalSDKWorker.js",
      serviceWorkerUpdaterPath: "/OneSignalSDKUpdaterWorker.js",
    });

    hasInitialized = true;
    attachWebDebugListeners();
    logFlow("Web OneSignal initialized", { appId });

    if (import.meta.env.VITE_ONESIGNAL_AUTO_PROMPT === "true") {
      await OneSignal.Slidedown.promptPush();
      logFlow("Web permission prompt shown");
    }
  } catch (error) {
    console.error("OneSignal initialization failed:", error);
  }
};

const getCurrentOneSignalUserId = (): string | null => {
  const onesignalId = OneSignal.User.onesignalId;
  const pushSubscriptionId = OneSignal.User.PushSubscription.id;
  return onesignalId ?? pushSubscriptionId ?? null;
};

const requestNativePermissionIfNeeded = async (
  nativeOneSignal: NativeOneSignal,
): Promise<boolean | null> => {
  if (nativePermissionRequestInFlight) {
    logFlow("Native permission request already in progress, awaiting existing request");
    return nativePermissionRequestInFlight;
  }

  nativePermissionRequestInFlight = (async () => {
    const canRequest = await nativeOneSignal.Notifications.canRequestPermission();
    logFlow("Native permission check during user sync", { canRequest });
    if (!canRequest) return null;

    const granted = await nativeOneSignal.Notifications.requestPermission(false);
    logFlow("Native permission prompt result during user sync", { granted });
    return granted;
  })();

  try {
    return await nativePermissionRequestInFlight;
  } finally {
    nativePermissionRequestInFlight = null;
  }
};

const persistOneSignalUserId = async (
  appUserId: string,
  oneSignalUserId: string,
): Promise<void> => {
  logFlow("Persisting OneSignal ID to profile", {
    appUserId,
    oneSignalUserId,
  });

  const { error } = await supabase
    .from("user_profiles")
    .update({ onesignal_user_id: oneSignalUserId } as any)
    .eq("id", appUserId);

  if (error) {
    console.error("Failed to store OneSignal user id:", error);
  } else {
    logFlow("OneSignal ID persisted to profile successfully", { appUserId });
  }
};

export const syncOneSignalUserIdToProfile = async (
  appUserId: string,
): Promise<void> => {
  const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
  const isNative = Capacitor.isNativePlatform();
  logFlow("syncOneSignalUserIdToProfile start", {
    appUserId,
    isNative,
    hasAppId: Boolean(appId),
  });
  if (!appId || typeof window === "undefined") {
    logFlow("syncOneSignalUserIdToProfile aborted: missing app id or window");
    return;
  }

  try {
    await initializeOneSignal();

    if (isNative) {
      const nativeOneSignal = await getNativeOneSignal();
      if (!nativeOneSignal) return;

      nativeOneSignal.login(appUserId);
      logFlow("Native OneSignal login called", { appUserId });
      await requestNativePermissionIfNeeded(nativeOneSignal);

      const [oneSignalUserId, externalId, pushSubscriptionId, pushToken] = await Promise.all([
        nativeOneSignal.User.getOnesignalId(),
        nativeOneSignal.User.getExternalId(),
        nativeOneSignal.User.pushSubscription.getIdAsync(),
        nativeOneSignal.User.pushSubscription.getTokenAsync(),
      ]);

      logFlow("Native OneSignal IDs after login", {
        oneSignalUserId,
        externalId,
        pushSubscriptionId,
        pushTokenPresent: Boolean(pushToken),
      });

      const idToPersist = oneSignalUserId ?? pushSubscriptionId ?? null;
      if (idToPersist) {
        await persistOneSignalUserId(appUserId, idToPersist);
        return;
      }

      logFlow("Native IDs not ready, waiting for change events");
      const onUserChange = async (event: { current?: { onesignalId?: string | null } }) => {
        const nextId = event.current?.onesignalId ?? (await nativeOneSignal.User.pushSubscription.getIdAsync()) ?? null;
        logFlow("Native delayed User change", { nextId });
        if (!nextId) return;
        await persistOneSignalUserId(appUserId, nextId);
        nativeOneSignal.User.removeEventListener("change", onUserChange);
      };

      const onPushChange = async (event: { current?: { id?: string | null } }) => {
        const nextId = event.current?.id ?? (await nativeOneSignal.User.getOnesignalId()) ?? null;
        logFlow("Native delayed PushSubscription change", { nextId });
        if (!nextId) return;
        await persistOneSignalUserId(appUserId, nextId);
        nativeOneSignal.User.pushSubscription.removeEventListener("change", onPushChange);
      };

      nativeOneSignal.User.addEventListener("change", onUserChange);
      nativeOneSignal.User.pushSubscription.addEventListener("change", onPushChange);
      setTimeout(() => {
        nativeOneSignal.User.removeEventListener("change", onUserChange);
        nativeOneSignal.User.pushSubscription.removeEventListener("change", onPushChange);
        logFlow("Native delayed listeners removed after timeout");
      }, 30000);
      return;
    }

    // Link OneSignal user to the authenticated app user for web SDK.
    await OneSignal.login(appUserId);
    logFlow("Web OneSignal login called", { appUserId });

    const currentOneSignalId = getCurrentOneSignalUserId();
    logFlow("Web current OneSignal ID after login", { currentOneSignalId });
    if (currentOneSignalId) {
      await persistOneSignalUserId(appUserId, currentOneSignalId);
      return;
    }

    const onUserChange = async (event: UserChangeEvent) => {
      const nextOneSignalId =
        event.current.onesignalId ?? OneSignal.User.PushSubscription.id ?? null;
      logFlow("Web delayed user change event", { nextOneSignalId });
      if (!nextOneSignalId) return;

      await persistOneSignalUserId(appUserId, nextOneSignalId);
      OneSignal.User.removeEventListener("change", onUserChange);
    };

    OneSignal.User.addEventListener("change", onUserChange);
    setTimeout(() => {
      OneSignal.User.removeEventListener("change", onUserChange);
      logFlow("Web delayed listener removed after timeout");
    }, 30000);
  } catch (error) {
    console.error("OneSignal profile sync failed:", error);
  }
};
